import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, ShieldOff, Loader2, AlertTriangle, Plus, Pencil, Trash2, RotateCw, X, Key, Building2, Lock, Unlock, Skull } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS, canonicalRole } from "@/lib/permissions";
import { PasswordStrengthMeter } from "@/components/security/PasswordStrengthMeter";
import { validate as validatePolicy } from "@/lib/passwordPolicy";

const ROLE_OPTIONS = [
  { value: "admin",       label: "Administrador",  description: "Acceso completo" },
  { value: "soc_analyst", label: "Analista SOC",   description: "Escanea, ve todo, gestiona automatizaciones" },
  { value: "read_only",   label: "Solo lectura",   description: "Ve todos los clientes; sin descargas" },
  { value: "client",      label: "Cliente",        description: "Ve SOLO su empresa, con descargas de PDF" },
];

export default function UsersManagement() {
  const { user } = useAuth();
  const isAdmin = canonicalRole(user?.role) === "admin";
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmMfa, setConfirmMfa] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Second, IRREVERSIBLE flow. ``Deshabilitar`` (confirmDelete above) just
  // flips ``active=false`` server-side; ``Eliminar`` (confirmPurge) hits the
  // new ``/admin/users/{id}/purge`` endpoint that hard-deletes from Mongo,
  // scrubs orphan ``assigned_admin_ids`` and revokes any open session.
  const [confirmPurge, setConfirmPurge] = useState(null);
  // Extra typed-in guard for the purge modal — the operator MUST type the
  // exact email of the user before the destructive button unlocks. Cheap
  // insurance against a misclick on a critical account.
  const [purgeTypedEmail, setPurgeTypedEmail] = useState("");
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [uR, cR] = await Promise.all([
        api.get("/admin/users"),
        api.get("/clients?sort=name").catch(() => ({ data: [] })),
      ]);
      setUsers(uR.data.users || []);
      setClients(cR.data || []);
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error(e?.response?.data?.detail || "No se pudo cargar usuarios");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);
  if (!isAdmin) return null;

  const doReactivate = async (u) => {
    try { await api.post(`/admin/users/${u.id}/reactivate`); toast.success(`${u.email} reactivado`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo reactivar"); }
  };
  const doResetMfa = async () => {
    if (!confirmMfa) return;
    try { const r = await api.post(`/admin/users/${confirmMfa.id}/reset-mfa`); toast.success(r.data.message || "MFA restablecido"); setConfirmMfa(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo restablecer MFA"); }
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    try { await api.delete(`/admin/users/${confirmDelete.id}`); toast.success(`${confirmDelete.email} desactivado`); setConfirmDelete(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo desactivar"); }
  };
  const doPurge = async () => {
    if (!confirmPurge) return;
    if (purgeTypedEmail.trim().toLowerCase() !== confirmPurge.email.toLowerCase()) {
      toast.error("El email tecleado no coincide");
      return;
    }
    setPurging(true);
    try {
      const r = await api.delete(`/admin/users/${confirmPurge.id}/purge`);
      const extras = [];
      if (r.data?.sessions_dropped) extras.push(`${r.data.sessions_dropped} sesión${r.data.sessions_dropped > 1 ? "es" : ""}`);
      if (r.data?.organizations_scrubbed) extras.push(`${r.data.organizations_scrubbed} asignación${r.data.organizations_scrubbed > 1 ? "es" : ""}`);
      const suffix = extras.length ? ` · purgado también: ${extras.join(", ")}` : "";
      toast.success(`${confirmPurge.email} eliminado permanentemente${suffix}`);
      setConfirmPurge(null); setPurgeTypedEmail(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    } finally { setPurging(false); }
  };
  const doUnlock = async (u) => {
    try { await api.post(`/admin/users/${u.id}/unlock`); toast.success(`${u.email} desbloqueado`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo desbloquear"); }
  };

  return (
    <div className="border border-border p-5 bg-card/50" data-testid="admin-users-section">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <div className="overline flex items-center gap-2 text-primary">
            <Users className="w-3 h-3" /> Gestión de usuarios
          </div>
          <h3 className="font-display text-xl font-medium mt-1">Usuarios del tenant</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-lg">
            Crea nuevas cuentas (incluye <span className="text-foreground">usuarios cliente</span> asociados a una empresa),
            cambia roles, restablece MFA y desactiva accesos.
          </p>
        </div>
        <button onClick={() => setOpenCreate(true)} data-testid="user-create-btn"
                className="border border-primary bg-primary text-primary-foreground px-4 py-2 font-mono text-[10px] tracking-widest uppercase hover:bg-primary/90 inline-flex items-center gap-2">
          <Plus className="w-3 h-3" /> Nuevo usuario
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" /> Cargando usuarios…
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono" data-testid="admin-users-table">
            <thead>
              <tr className="text-left overline text-muted-foreground border-b border-border">
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Nombre</th>
                <th className="py-2 pr-4">Rol · Cliente</th>
                <th className="py-2 pr-4">Estado</th>
                <th className="py-2 pr-4">MFA</th>
                <th className="py-2 pr-4">Último acceso</th>
                <th className="py-2 pr-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const inactive = u.active === false;
                const role = canonicalRole(u.role);
                return (
                  <tr key={u.id} className={`border-b border-border/50 hover:bg-muted/30 ${inactive ? "opacity-50" : ""}`} data-testid={`admin-user-row-${u.email}`}>
                    <td className="py-2 pr-4">
                      {u.email}
                      {u.id === user.id && (<span className="ml-2 text-[10px] uppercase text-muted-foreground tracking-widest">(tú)</span>)}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">{u.name || "—"}</td>
                    <td className="py-2 pr-4">
                      <div className="flex flex-col gap-1">
                        <span className="px-1.5 py-0.5 border border-primary/50 text-primary bg-primary/5 text-[10px] tracking-widest uppercase w-fit">
                          {ROLE_LABELS[role] || u.role}
                        </span>
                        {role === "client" && u.organization_name && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground" data-testid={`user-client-${u.email}`}>
                            <Building2 className="w-3 h-3" /> {u.organization_name}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-4">
                      {inactive ? (
                        <span className="px-1.5 py-0.5 border border-sev-medium/50 text-sev-medium bg-sev-medium/10 text-[10px] tracking-widest uppercase">Desactivado</span>
                      ) : u.locked_permanent || u.lockout_level >= 3 ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-sev-critical/60 text-sev-critical bg-sev-critical/10 text-[10px] tracking-widest uppercase"
                              data-testid={`user-locked-permanent-${u.email}`}>
                          <Lock className="w-3 h-3" /> Bloqueado permanente
                        </span>
                      ) : u.locked_until && new Date(u.locked_until) > new Date() ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-sev-high/60 text-sev-high bg-sev-high/10 text-[10px] tracking-widest uppercase"
                              data-testid={`user-locked-temp-${u.email}`}>
                          <Lock className="w-3 h-3" /> Bloqueado nivel {u.lockout_level}
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 border border-sev-low/50 text-sev-low bg-sev-low/10 text-[10px] tracking-widest uppercase">Activo</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {u.mfa_enabled ? `Activo · ${u.backup_codes_remaining} backup` : "Inactivo"}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {u.last_login ? new Date(u.last_login).toLocaleString() : "—"}
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setEditingUser(u)} data-testid={`user-edit-btn-${u.email}`}
                                className="border border-border p-1.5 hover:border-primary hover:text-primary" title="Editar usuario">
                          <Pencil className="w-3 h-3" />
                        </button>
                        {(u.locked_permanent || (u.locked_until && new Date(u.locked_until) > new Date())) && (
                          <button onClick={() => doUnlock(u)} data-testid={`user-unlock-btn-${u.email}`}
                                  className="border border-sev-high/50 text-sev-high p-1.5 hover:bg-sev-high/10"
                                  title="Desbloquear cuenta (admin)">
                            <Unlock className="w-3 h-3" />
                          </button>
                        )}
                        <button disabled={!u.mfa_enabled || u.id === user.id} onClick={() => setConfirmMfa(u)}
                                data-testid={`user-reset-mfa-btn-${u.email}`}
                                className="border border-border p-1.5 hover:border-sev-medium hover:text-sev-medium disabled:opacity-30 disabled:cursor-not-allowed"
                                title={u.id === user.id ? "Usa /settings > MFA para tu propia cuenta" : (u.mfa_enabled ? "Restablecer MFA" : "Sin MFA activo")}>
                          <ShieldOff className="w-3 h-3" />
                        </button>
                        {inactive ? (
                          <button onClick={() => doReactivate(u)} data-testid={`user-reactivate-btn-${u.email}`}
                                  className="border border-sev-low/50 text-sev-low p-1.5 hover:bg-sev-low/10" title="Reactivar cuenta">
                            <RotateCw className="w-3 h-3" />
                          </button>
                        ) : (
                          <button onClick={() => setConfirmDelete(u)} disabled={u.id === user.id} data-testid={`user-delete-btn-${u.email}`}
                                  className="border border-border p-1.5 hover:border-sev-medium hover:text-sev-medium disabled:opacity-30 disabled:cursor-not-allowed"
                                  title={u.id === user.id ? "No puedes deshabilitar tu propia cuenta" : "Deshabilitar cuenta (reversible)"}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                        {/* Permanent hard-delete — separate button so it can never
                            fire from a misclick on the (reversible) Deshabilitar
                            action. Available on BOTH active and inactive rows. */}
                        <button onClick={() => { setConfirmPurge(u); setPurgeTypedEmail(""); }}
                                disabled={u.id === user.id}
                                data-testid={`user-purge-btn-${u.email}`}
                                className="border border-sev-critical/40 text-sev-critical p-1.5 hover:bg-sev-critical/10 disabled:opacity-30 disabled:cursor-not-allowed"
                                title={u.id === user.id ? "No puedes eliminar tu propia cuenta" : "Eliminar permanentemente (irreversible)"}>
                          <Skull className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (<tr><td colSpan={7} className="py-4 text-center text-muted-foreground">Sin usuarios.</td></tr>)}
            </tbody>
          </table>
        </div>
      )}

      {openCreate && (
        <UserFormModal mode="create" clients={clients}
          onClose={() => setOpenCreate(false)}
          onSaved={() => { setOpenCreate(false); load(); }} />
      )}
      {editingUser && (
        <UserFormModal mode="edit" user={editingUser} clients={clients}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); load(); }} />
      )}

      {confirmMfa && (
        <ConfirmModal testid="admin-reset-mfa-modal" title="Restablecer MFA" intent="warning"
          icon={<AlertTriangle className="w-4 h-4" />}
          onCancel={() => setConfirmMfa(null)} onConfirm={doResetMfa}
          confirmLabel="Restablecer" confirmTestId="admin-reset-mfa-confirm-btn">
          <p className="text-sm">¿Restablecer la MFA de <span className="font-mono font-bold">{confirmMfa.email}</span>?</p>
          <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
            <li>Se borrará su secreto TOTP y los códigos de respaldo.</li>
            <li>Podrá volver a activar MFA en su próximo inicio de sesión.</li>
          </ul>
        </ConfirmModal>
      )}

      {confirmDelete && (
        <ConfirmModal testid="admin-delete-user-modal" title="Deshabilitar cuenta" intent="danger"
          icon={<Trash2 className="w-4 h-4" />}
          onCancel={() => setConfirmDelete(null)} onConfirm={doDelete}
          confirmLabel="Deshabilitar" confirmTestId="admin-delete-user-confirm-btn">
          <p className="text-sm">¿Deshabilitar a <span className="font-mono font-bold">{confirmDelete.email}</span>?</p>
          <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
            <li>El usuario no podrá iniciar sesión.</li>
            <li>La acción es reversible (botón <RotateCw className="w-3 h-3 inline" /> reactivar).</li>
          </ul>
        </ConfirmModal>
      )}

      {confirmPurge && (
        <ConfirmModal testid="admin-purge-user-modal" title="Eliminar permanentemente" intent="danger"
          icon={<Skull className="w-4 h-4" />}
          onCancel={() => { setConfirmPurge(null); setPurgeTypedEmail(""); }}
          onConfirm={doPurge}
          confirmLabel={purging ? "Eliminando…" : "Eliminar para siempre"}
          confirmDisabled={purging || purgeTypedEmail.trim().toLowerCase() !== confirmPurge.email.toLowerCase()}
          confirmTestId="admin-purge-user-confirm-btn">
          <div className="border border-sev-critical/60 bg-sev-critical/10 p-3">
            <p className="text-xs font-mono uppercase tracking-widest text-sev-critical mb-1.5 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> Acción irreversible
            </p>
            <p className="text-sm">
              Vas a borrar la cuenta <span className="font-mono font-bold">{confirmPurge.email}</span> de
              la base de datos.
            </p>
          </div>
          <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
            <li>El documento se elimina físicamente — no se puede recuperar.</li>
            <li>Se revocan sus sesiones abiertas y se limpian sus asignaciones a clientes.</li>
            <li>Si buscas algo reversible usa <span className="text-foreground">Deshabilitar</span> en su lugar.</li>
          </ul>
          <label className="block space-y-1.5">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Escribe <span className="text-sev-critical">{confirmPurge.email}</span> para confirmar
            </span>
            <input type="text" autoFocus value={purgeTypedEmail}
                   onChange={(e) => setPurgeTypedEmail(e.target.value)}
                   data-testid="admin-purge-user-email-input"
                   className="w-full bg-background border border-border px-3 py-2 font-mono text-xs focus:border-sev-critical outline-none"
                   placeholder={confirmPurge.email} />
          </label>
        </ConfirmModal>
      )}
    </div>
  );
}

function UserFormModal({ mode, user, clients, onClose, onSaved }) {
  const isCreate = mode === "create";
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [role, setRole] = useState(canonicalRole(user?.role) || "read_only");
  const [organizationId, setOrganizationId] = useState(user?.organization_id || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (role === "client" && !organizationId) {
      toast.error("Selecciona el cliente al que pertenece este usuario");
      return;
    }
    // Only validate the policy when a password is actually being set
    if (password) {
      const v = validatePolicy(password);
      if (!v.ok) {
        toast.error("Contraseña no válida: " + v.errors[0]);
        return;
      }
    }
    setBusy(true);
    try {
      if (isCreate) {
        const body = { email: email.trim().toLowerCase(), name, role, password };
        if (role === "client") body.organization_id = organizationId;
        await api.post("/admin/users", body);
        toast.success("Usuario creado");
      } else {
        const body = { name, role };
        if (password) body.password = password;
        if (role === "client") body.organization_id = organizationId;
        await api.patch(`/admin/users/${user.id}`, body);
        toast.success("Usuario actualizado");
      }
      onSaved();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid={`user-form-modal-${mode}`}>
      <form onSubmit={submit} className="bg-background border border-border max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="overline text-primary">{isCreate ? "Nuevo usuario" : "Editar usuario"}</div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-muted" data-testid="user-form-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Email</div>
          <input value={email} onChange={e => setEmail(e.target.value)} required disabled={!isCreate} type="email"
                 className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none disabled:opacity-50"
                 data-testid="user-form-email" />
        </label>

        <label className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Nombre</div>
          <input value={name} onChange={e => setName(e.target.value)} required
                 className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                 data-testid="user-form-name" />
        </label>

        <div className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Rol</div>
          <div className="space-y-1">
            {ROLE_OPTIONS.map(o => (
              <label key={o.value}
                     className={`flex items-start gap-2 border p-2 cursor-pointer ${role === o.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                     data-testid={`user-form-role-${o.value}`}>
                <input type="radio" name="role" value={o.value} checked={role === o.value} onChange={() => setRole(o.value)} className="mt-1" />
                <div>
                  <div className="text-sm">{o.label}</div>
                  <div className="text-[10px] text-muted-foreground">{o.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {role === "client" && (
          <label className="block" data-testid="user-form-client-selector">
            <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1 flex items-center gap-1">
              <Building2 className="w-3 h-3" /> Cliente asociado (obligatorio)
            </div>
            <select value={organizationId} onChange={e => setOrganizationId(e.target.value)} required
                    className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                    data-testid="user-form-organization-id">
              <option value="">— Selecciona la empresa que contrató el servicio —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id} data-testid={`user-form-client-opt-${c.id}`}>{c.name}</option>
              ))}
            </select>
            <div className="text-[10px] text-muted-foreground mt-1">
              Este usuario podrá ver ÚNICAMENTE la información de la empresa seleccionada.
            </div>
          </label>
        )}

        <label className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1 flex items-center gap-1">
            <Key className="w-3 h-3" /> {isCreate ? "Contraseña" : "Nueva contraseña (opcional)"}
          </div>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                 required={isCreate} minLength={14}
                 placeholder={isCreate ? "Mínimo 14 caracteres" : "Dejar en blanco para no cambiar"}
                 className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                 data-testid="user-form-password" />
          {(isCreate || password) && <PasswordStrengthMeter password={password} />}
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">Cancelar</button>
          <button type="submit" disabled={busy}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="user-form-submit">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
            {isCreate ? "Crear" : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ConfirmModal({ testid, title, intent, icon, onCancel, onConfirm, confirmLabel, confirmTestId, confirmDisabled, children }) {
  const [busy, setBusy] = useState(false);
  const borderClass = intent === "danger" ? "border-sev-critical/50" : "border-sev-medium/50";
  const btnClass = intent === "danger" ? "bg-sev-critical" : "bg-sev-medium";
  const titleClass = intent === "danger" ? "text-sev-critical" : "text-sev-medium";
  const doConfirm = async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid={testid}>
      <div className={`bg-background border ${borderClass} max-w-md w-full p-6 space-y-4`}>
        <div className={`flex items-center gap-2 overline ${titleClass}`}>{icon} {title}</div>
        {children}
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">Cancelar</button>
          <button onClick={doConfirm} disabled={busy || confirmDisabled} data-testid={confirmTestId}
                  className={`${btnClass} text-primary-foreground px-4 py-2 text-sm font-mono tracking-widest uppercase hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2`}>
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
